import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvDropdownClientSideElement,
  DvDynamicFormModule,
  DvFormUtils,
  DvSettingsListGroup,
  DvSettingsListGroupLink,
  DvSettingsListModule,
  FormOf,
} from '@devinco/ui/legacy';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  accessControlMenuItemLinks,
  accessControlMenuItemTitle,
} from './access-control-settings/constants';
import {
  assetsMenuItemLinks,
  assetsMenuItemTitle,
} from './assets-settings/constants';
import {
  departmentMenuItemLinks,
  departmentMenuItemTitle,
} from './department-settings/constants';
import { combineLatest, take } from 'rxjs';
import {
  alertsMenuItemLinks,
  alertsMenuItemTitle,
} from './alerts-settings/constants';
import {
  hardwareDevicesMenuItemLinks,
  hardwareDevicesMenuItemTitle,
} from './hardware-devices-settings/constants';
import { SettingsSearchDropdownItem } from './interfaces';
import { tagMenuItemLinks, tagMenuItemTitle } from './tag-settings/constants';
import {
  tripMenuItemLinks,
  tripMenuItemTitle,
} from './trip-settings/constants';
import {
  onCallMenuItemLinks,
  onCallMenuItemTitle,
} from './on-call-settings/constants';
import { FeaturesService } from '@core/services/features.service';
import { Feature } from '@shared/constants/feature';
import {
  fleetMenuItemLinks,
  fleetMenuItemTitle,
} from './fleet-settings/constants';
import { PipesModule } from '@shared/pipes/pipes.module';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import {
  AccessControlSettingsRouteName,
  AlertSettingsRouteName,
  AssetSettingsRouteName,
  DepartmentSettingsRouteName,
  FleetSettingsRouteName,
  HardwareDevicesSettingsRouteName,
  OnCallRouteName,
  TagSettingsRouteName,
  TripSettingsRouteName,
} from './settings-route-names';
import {
  integrationsMenuItemLinks,
  integrationsMenuItemTitle,
} from './integration-settings/constants';
import { IntegrationSettingsRouteName } from './integration-settings/integration-settings.routes';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { SupportUserService } from '@shared/services/support-user.service';

@Component({
  imports: [
    DvDynamicFormModule,
    DvSettingsListModule,
    PipesModule,
    TranslateModule,
  ],
  selector: 'app-settings-page',
  templateUrl: './settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./settings-page.component.scss', '../../styles/page.scss'],
})
export class SettingsPageComponent implements OnInit {
  private translate = inject(TranslateService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private featuresService = inject(FeaturesService);
  private userMetaDataService = inject(UserMetadataService);
  private supportUserService = inject(SupportUserService);

  settingsListItems = signal<DvSettingsListGroup[]>([]);
  searchElement: DvDynamicFormElement;
  searchInputForm: FormGroup<FormOf<{ settingsLink: string }>>;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    combineLatest([
      this.permissionsService.getUserRolesAndPermissions(),
      this.featuresService.getFeatures(),
      this.supportUserService.isSupportUser(),
    ])
      .pipe(take(1))
      .subscribe(([rolesAndPerms, feats, isSupportUser]) => {
        this.settingsListItems.set(
          this.getSettingsListItems(
            rolesAndPerms.Permissions,
            feats,
            isSupportUser
          )
        );
        this.setupSettingsSearchDropdown();
      });
  }

  private getSettingsListItems(
    permissions: string[],
    features: Feature[],
    isSupportUser: boolean
  ): DvSettingsListGroup[] {
    return [
      {
        description: this.translate.instant(departmentMenuItemTitle),
        icon: 'company_settings',
        iconFont: 'dv-icons',
        links: departmentMenuItemLinks.map(link => ({
          routerLink: `${DepartmentSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
        })),
        hide: !permissions.includes(RolesAndPermissions.DepartmentSettingsRead),
      },
      {
        description: this.translate.instant(tripMenuItemTitle),
        icon: 'cash_check',
        iconFont: 'dv-icons',
        links: tripMenuItemLinks.map(link => ({
          routerLink: `${TripSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide: this.handleTripSettingsVisibility(permissions, link.routerLink),
        })),
      },
      {
        description: this.translate.instant(accessControlMenuItemTitle),
        icon: 'account_lock',
        iconFont: 'dv-icons',
        links: accessControlMenuItemLinks.map(link => ({
          routerLink: `${AccessControlSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide:
            (link.routerLink === AccessControlSettingsRouteName.Roles &&
              !permissions.includes(RolesAndPermissions.RoleSettingsRead)) ||
            (link.routerLink === AccessControlSettingsRouteName.UserLog &&
              !permissions.includes(RolesAndPermissions.UserLogSettingsRead)),
        })),
      },
      {
        description: this.translate.instant(tagMenuItemTitle),
        icon: 'tag',
        iconFont: 'dv-icons',
        links: tagMenuItemLinks.map(link => ({
          routerLink: `${TagSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
        })),
      },
      {
        description: this.translate.instant(alertsMenuItemTitle),
        icon: 'notification_alert',
        iconFont: 'dv-icons',
        links: alertsMenuItemLinks.map(link => ({
          routerLink: `${AlertSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
        })),
        hide: !permissions.includes(RolesAndPermissions.AlertSettingsRead),
      },
      {
        description: this.translate.instant(hardwareDevicesMenuItemTitle),
        icon: 'developer_board',
        iconFont: 'material-icons-outlined',
        links: hardwareDevicesMenuItemLinks.map(link => ({
          routerLink: `${HardwareDevicesSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide: this.handleHardwareDevicesVisibility(
            permissions,
            link.routerLink
          ),
        })),
      },
      {
        description: this.translate.instant(assetsMenuItemTitle),
        icon: 'viatracks_find',
        iconFont: 'dv-icons',
        links: assetsMenuItemLinks.map(link => ({
          routerLink: `${AssetSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide:
            (link.routerLink === AssetSettingsRouteName.AssetType &&
              !permissions.includes(
                RolesAndPermissions.AssetTypeSettingsRead
              )) ||
            (link.routerLink === AssetSettingsRouteName.AssetLocation &&
              !permissions.includes(
                RolesAndPermissions.AssetLocationSettingsRead
              )),
        })),
      },
      {
        description: this.translate.instant(integrationsMenuItemTitle),
        icon: 'vipps',
        iconFont: 'dv-icons',
        links: integrationsMenuItemLinks.map(link => ({
          routerLink: `${IntegrationSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
        })),
        hide: !(isSupportUser && this.userMetaDataService.hasSkiDomain()),
      },
      {
        description: this.translate.instant(onCallMenuItemTitle),
        icon: 'ring_volume',
        iconFont: 'dv-icons',
        links: onCallMenuItemLinks.map(link => ({
          routerLink: `${OnCallRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide:
            link.permissions &&
            !link.permissions.every(permission =>
              permissions.includes(permission)
            ),
        })),
        hide:
          !features.includes(Feature.Standby) ||
          !permissions.includes(RolesAndPermissions.StandbySettingsEdit),
      },
      {
        description: this.translate.instant(fleetMenuItemTitle),
        icon: 'map',
        iconFont: 'dv-icons',
        links: fleetMenuItemLinks.map(link => ({
          routerLink: `${FleetSettingsRouteName.Main + '/' + link.routerLink}`,
          label: this.translate.instant(link.label),
          hide: !permissions.includes(
            RolesAndPermissions.FleetSettingsGeofenceRead
          ),
        })),
        hide: !features.includes(Feature.Fleet),
      },
    ].filter(settingsListItem => {
      return (
        !!settingsListItem.links.length &&
        (settingsListItem.links as DvSettingsListGroupLink[]).some(
          link => !link.hide
        )
      );
    }) as DvSettingsListGroup[];
  }

  private handleHardwareDevicesVisibility(
    permissions: string[],
    url?: string
  ): boolean {
    switch (url) {
      case HardwareDevicesSettingsRouteName.FindTags:
        return !permissions.includes(RolesAndPermissions.FindSettingsRead);
      case HardwareDevicesSettingsRouteName.Trackers:
        return !permissions.includes(RolesAndPermissions.TrackerSettingsRead);
      case HardwareDevicesSettingsRouteName.CardReaders:
        return !permissions.includes(
          RolesAndPermissions.CardReaderSettingsRead
        );
      case HardwareDevicesSettingsRouteName.Keyfobs:
        return !permissions.includes(RolesAndPermissions.KeyfobSettingsRead);
      default:
        return true;
    }
  }

  private handleTripSettingsVisibility(
    permissions: string[],
    url?: string
  ): boolean {
    switch (url) {
      case TripSettingsRouteName.Expenses:
        return !permissions.includes(
          RolesAndPermissions.TripExpenseSettingsRead
        );
      case TripSettingsRouteName.FuelPrices:
        return !permissions.includes(RolesAndPermissions.TripFuelSettingsRead);

      case TripSettingsRouteName.TripTypeSetup:
        return !permissions.includes(
          RolesAndPermissions.TripSettingsTypeSetupRead
        );

      default:
        return false;
    }
  }

  private setupSettingsSearchDropdown(): void {
    this.searchElement = new DvDropdownClientSideElement({
      key: 'settingsLink',
      colspan: 3,
      placeholder: 'Grid.searchOoo',
      valueKey: 'settingsLink',
      labelKeys: ['label'],
      items: this.getSettingsDropdownItems(this.settingsListItems()),
    });

    this.searchInputForm = DvFormUtils.createFormGroup([this.searchElement]);
    this.listenToSearchDropdownChanges();
  }

  private getSettingsDropdownItems(
    settingsListItems: DvSettingsListGroup[]
  ): SettingsSearchDropdownItem[] {
    const itemArray: SettingsSearchDropdownItem[] = [];

    settingsListItems
      .filter(settingsItem => !settingsItem.hide)
      .forEach(settingsItem => {
        const links = (settingsItem.links || [])
          .filter(link => !link.hide)
          .map(
            link =>
              ({
                label: this.translate.instant(link.label),
                settingsLink: link.routerLink || link.externalLink,
              }) as SettingsSearchDropdownItem
          );
        itemArray.push(...links);
      });

    return itemArray.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }

  private listenToSearchDropdownChanges(): void {
    this.searchInputForm.controls.settingsLink.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(settingsLink => {
        this.router.navigate([settingsLink], {
          relativeTo: this.activatedRoute,
        });
      });
  }
}
